import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import { Outlet, type RouteObject } from 'react-router'
import type { AccessPair } from '@/shared/lib'
import { AppLayout } from './AppLayout'
import { GuestOnly } from './GuestOnly'
import { RouteGuard } from './RouteGuard'
import { CABINET_ROUTES } from './routeRegistry'
import { SessionGate } from './SessionGate'

/*
 * Каждая страница — отдельный чанк. React.lazy, а не route.lazy: граница ожидания кабинета
 * стоит внутри раскладки (вокруг <Outlet />), поэтому при переходе меняется только содержимое,
 * а шапка и меню остаются на месте. Экраны до входа ждут в общей границе `App`.
 */
const LoginPage = lazy(() => import('@/pages/login').then((m) => ({ default: m.LoginPage })))
const RequestAccessPage = lazy(() =>
  import('@/pages/request-access').then((m) => ({ default: m.RequestAccessPage })),
)
const RegisterPage = lazy(() =>
  import('@/pages/register').then((m) => ({ default: m.RegisterPage })),
)
const InvitePage = lazy(() => import('@/pages/invite').then((m) => ({ default: m.InvitePage })))
const SelectCompanyPage = lazy(() =>
  import('@/pages/select-company').then((m) => ({ default: m.SelectCompanyPage })),
)
const NotFoundPage = lazy(() =>
  import('@/pages/not-found').then((m) => ({ default: m.NotFoundPage })),
)

/*
 * Витрина UI Kit нужна только разработке. Сам import() обязан лежать внутри DEV-ветки:
 * если поднять его в const наверху файла, сборщик оставит чанк стенда вместе со всеми
 * *.demo.tsx в прод-артефакте, даже когда маршрут выключен.
 *
 * Стенд живёт вне раскладки кабинета и без сеанса: у него своя навигация по компонентам.
 */
const devRoutes: RouteObject[] = import.meta.env.DEV
  ? [
      {
        path: '/dev/ui-kit',
        lazy: async () => {
          const { DevUiKitPage } = await import('@/pages/dev-ui-kit')
          return { Component: DevUiKitPage }
        },
      },
    ]
  : []

/** Страница реестра под гардом её пары: `null` — любой вошедший. */
function guarded(access: AccessPair | null, Page: LazyExoticComponent<ComponentType>) {
  return access === null ? (
    <Page />
  ) : (
    <RouteGuard access={access}>
      <Page />
    </RouteGuard>
  )
}

/**
 * Маршруты приложения. Браузерный роутер из них собирает `App`, тесты — `createMemoryRouter`:
 * у каждого сценария свой роутер и своя история, состояние между тестами не течёт.
 */
export const routes: RouteObject[] = [
  ...devRoutes,

  {
    // До входа: вход и заявка на доступ. Живой сеанс отсюда уводит в кабинет
    element: (
      <GuestOnly>
        <Outlet />
      </GuestOnly>
    ),
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/request-access', element: <RequestAccessPage /> },
      { path: '/register', element: <RegisterPage /> },
    ],
  },

  // Ссылка из письма открывается и без сеанса: пароль задаёт тот, кого пригласили
  { path: '/invite/:token', element: <InvitePage /> },

  {
    // Выбор компании: сеанс нужен, выбранная компания — нет
    element: (
      <SessionGate>
        <Outlet />
      </SessionGate>
    ),
    children: [{ path: '/select-company', element: <SelectCompanyPage /> }],
  },

  {
    // Кабинет: сеанс и выбранная компания обязательны. Раскладка одна на все экраны
    element: (
      <SessionGate>
        <AppLayout />
      </SessionGate>
    ),
    children: [
      ...CABINET_ROUTES.map<RouteObject>(({ path, access, Page, children }) => ({
        path,
        element: guarded(access, Page),
        // Вкладки профиля: гард раскладки уже пропустил, у каждой вкладки — свой
        ...(children !== undefined
          ? {
              children: children.map<RouteObject>((child) =>
                child.path === undefined
                  ? { index: true, element: guarded(child.access, child.Page) }
                  : { path: child.path, element: guarded(child.access, child.Page) },
              ),
            }
          : {}),
      })),

      // Промах адресом не отбирает у пользователя навигацию: 404 рисуется внутри каркаса
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]
